import React, {
    useEffect,
    useLayoutEffect,
    useMemo,
    useRef,
    useState
} from 'react';
import {
    useDispatch,
    useSelector
} from 'react-redux';
import {keys} from 'ts-transformer-keys';

import {Button} from 'primereact/button';
import {Menubar} from 'primereact/menubar';
import {
    MenuItem,
    MenuItemCommandEvent
} from 'primereact/menuitem';
import {Tooltip} from 'primereact/tooltip';

import {__} from '~/shared/src/utilities/messageCatalog';

import {resolveLigatureIcon} from '~/frontend/src/utilities/renderHelpers';

import {
    store,
    StoreState
} from '~/frontend/src/store';
import openPanel from '~/frontend/src/actions/panelManager/openPanel';
import showPanelPropsDialog from '~/frontend/src/actions/panelManager/showPanelPropsDialog';


type DefaultProps = {
    showAsBurger? : boolean;
}
const defaultProps : Required<DefaultProps> = {
    showAsBurger: false
};

type Props = {};

export type ComponentProps = DefaultProps & Props;
export const componentProps = keys<ComponentProps>();

type AllProps = Required<DefaultProps> & Props;


/**
 * page main menu bar
 * @return {JSX.Element}
 * @constructor
 */
const PageMainMenu = ( props : AllProps ) => {
    const dispatch                                                    = useDispatch(),
          {
              showAsBurger
          }                                                           = props,
          user                                                        = useSelector( ( state : StoreState ) => state.session?.user ? state.session.user : undefined ),
          activePanelKey                                              = useSelector( ( state : StoreState ) => state.panelManager?.activePanelKey ?? '' ),
          cResizeToggle                                               = useRef<number>( 0 ),
          [ resizeToggle, setResizeToggle ]                           = useState<number>( 0 ),
          [ hideShowPanelConfigButton, setHideShowPanelConfigButton ] = useState<boolean>( false ),
          [ showMenuAsBurger, setShowMenuAsBurger ]                   = useState<boolean>( false ),
          [ remWidth, setRemWidth ]                                   = useState<number>( 12 ),
          localizeAndCommandizeMenuRecursive                          = ( menuItems : MenuItem[], tooltipsRef : React.ReactNode[] ) : MenuItem[] => {
              // de-reference array
              const retVal = ( [] as MenuItem[] ).concat( menuItems );

              for ( let i = 0; i < retVal.length; i++ ) {
                  // de-reference object
                  retVal[ i ] = { ...retVal[ i ] };

                  const menuItem = retVal[ i ];

                  if ( menuItem.label ) {
                      menuItem.label = __( menuItem.label );
                  }

                  if ( menuItem.icon ) {
                      menuItem.icon = resolveLigatureIcon( `p-menuitem-icon ${menuItem.icon}` );
                  }

                  menuItem.command = ( event : MenuItemCommandEvent ) => {
                      const panelConfig = user!.getPanelConfigFromIdList( event.item.data?.panelConfigIds ?? [] );

                      if ( panelConfig ) {
                          store.dispatch( openPanel( panelConfig ) );
                      }
                  };

                  if ( menuItem.data?.tooltip ) {
                      tooltipsRef.push(
                          <Tooltip
                              key={`${menuItem.className}-tooltip`}
                              target={`.${menuItem.className}`}
                              my="left bottom"
                              at="left top"
                              className="yn-tooltip"
                              autoHide
                              showDelay={750}
                          >
                              {__( menuItem.data.tooltip )}
                          </Tooltip>
                      );
                  }

                  if ( menuItem.items ) {
                      menuItem.items = localizeAndCommandizeMenuRecursive( menuItem.items as MenuItem[], tooltipsRef );
                  }
              }

              return retVal;
          },
          isVerticalTabMenu = useMemo(
              () => {
                  return user?.tabBarPosition === 'vertical';
              },
              [
                  user?.tabBarPosition
              ]
          ),
          { menuItems, tooltips }                                     = useMemo(
              () => {
                  const retVal = {
                      menuItems: [] as MenuItem[],
                      tooltips : [] as React.ReactNode[]
                  };

                  if ( user?.mainMenu?.length ) {
                      retVal.menuItems = localizeAndCommandizeMenuRecursive( user.mainMenu, retVal.tooltips );
                  }

                  return retVal;
              },
              [
                  user?.mainMenu ?? [],
                  user?.language ?? ''
              ]
          ),
          end                                                         = useMemo(
              () => {
                  const retVal : React.ReactNode[] = [];

                  // add show panel config button for admins only
                  if ( !showAsBurger && !hideShowPanelConfigButton && activePanelKey && config.sysGroups?.admin && user?.isMemberOfGroup( config.sysGroups.admin ) ) {
                      retVal.push( <Button
                          key="pageMainMenu_showPanelConfigButton"
                          id="pageMainMenu_showPanelConfigButton"
                          label="Show panel config"
                          onClick={() => dispatch( showPanelPropsDialog( activePanelKey ) )}
                          className="p-button-text"
                          style={{ width: '12rem' }}
                      /> );
                  }

                  return retVal;
              },
              [
                  activePanelKey,
                  hideShowPanelConfigButton,
                  showAsBurger,
                  user
              ]
          ),
          menu                                                        = useMemo(
              () => {
                  return user
                      ? <Menubar
                          key="pageMainMenu"
                          id="pageMainMenu"
                          model={menuItems}
                          end={end}
                          className={`yn-page-menu${isVerticalTabMenu ? ' yn-page-tabview-vertical' : ''}${showAsBurger ? ' yn-show-always-as-burger' : ''}${showMenuAsBurger ? ' yn-show-as-burger' : ''}`}
                      />
                      : null;
              },
              [
                  end,
                  isVerticalTabMenu,
                  menuItems,
                  showAsBurger,
                  showMenuAsBurger,
                  user
              ]
          );

    cResizeToggle.current = resizeToggle;

    // add/remove listener for resizing
    useEffect(
        () => {
            const windowResizeHandler = () => {
                setResizeToggle( cResizeToggle.current + 1 );
            };

            setResizeToggle( cResizeToggle.current + 1 );

            window.addEventListener( 'resize', windowResizeHandler );
            return () => {
                window.removeEventListener( 'resize', windowResizeHandler );
            }
        },
        []
    );

    // get remWidth (on update)
    useLayoutEffect(
        () => {
            const menuElement    = document.getElementById( 'pageMainMenu' ),
                  computedStyles = menuElement ? window.getComputedStyle( menuElement ) : undefined;

            if ( computedStyles?.fontSize ) {
                const fontSize = Number( computedStyles.fontSize.substring( 0, computedStyles.fontSize.length - 2 ) );

                if ( fontSize !== remWidth ) {
                    setRemWidth( fontSize );
                }
            }
        }
    );

    // set hideShowPanelConfigButton and showMenuAsBurger
    // by testing menu width against available width
    useLayoutEffect(
        () => {
            const menuElement                = document.getElementById( 'pageMainMenu' ),
                  menuListElement            = menuElement?.querySelector( 'ul.p-menubar-root-list' ) as HTMLElement,
                  showPanelConfigButtonWidth = 12 * remWidth; // buttonWidth 12rem

            if ( menuElement && menuListElement ) {
                const hasYnShowAlwaysAsBurger = menuElement.classList.contains( 'yn-show-always-as-burger' ),
                      hasYnShowAsBurger       = menuElement.classList.contains( 'yn-show-as-burger' );

                menuElement.classList.remove( 'yn-show-always-as-burger' );
                menuElement.classList.remove( 'yn-show-as-burger' );

                const menuWidth = menuListElement.clientWidth + remWidth; // paddingLeft/Right: 1rem

                if ( hasYnShowAlwaysAsBurger ) {
                    menuElement.classList.add( 'yn-show-always-as-burger' );
                }
                if ( hasYnShowAsBurger ) {
                    menuElement.classList.add( 'yn-show-as-burger' );
                }

                if ( !hideShowPanelConfigButton && menuWidth + showPanelConfigButtonWidth > document.documentElement.clientWidth ) {
                    setHideShowPanelConfigButton( true );
                }
                else if ( hideShowPanelConfigButton && menuWidth + showPanelConfigButtonWidth <= document.documentElement.clientWidth ) {
                    setHideShowPanelConfigButton( false );
                }

                if ( !showMenuAsBurger && menuWidth >= document.documentElement.clientWidth ) {
                    setShowMenuAsBurger( true );
                }
                else if ( showMenuAsBurger && menuWidth < document.documentElement.clientWidth ) {
                    setShowMenuAsBurger( false );
                }
            }
        },
        [
            remWidth,
            resizeToggle,
            menuItems
        ]
    );

    return <>
        {tooltips}
        {menu}
        {showAsBurger &&
            <div className="yn-page-menu-shadow"/>
        }
    </>;
}
PageMainMenu.defaultProps = defaultProps;
PageMainMenu.displayName = 'PageMainMenu';

export default PageMainMenu;
